import {
  authControllerLogoutCookie,
  type GetMyOptionsDtoRes,
  userControllerGetMyOptions,
} from "@/shared/api/generated"
import { UserPlan, type UserPayload } from "@/shared/types"
import { decodeJwt } from "@/shared/utils"
import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { useOptionsStore } from "../options"
import { queryClient } from "@/shared/api/query-client"
import { activePlayer } from "../audio-engine"
import { setSentryUser } from "../sentry"

const INITIAL_USER_PAYLOAD: StoreData = {
  access_token: "",
  email: null,
  isEmailConfirmed: false,
  isGuest: false,
  plan: UserPlan.FREE,
}

interface StoreData {
  access_token: string
  email: string | null
  isEmailConfirmed: boolean
  isGuest: boolean
  plan: UserPlan
}

interface StoreActions {
  setAccessToken: (access_token: string) => UserPayload
  login: (
    access_token: string,
    options?: GetMyOptionsDtoRes
  ) => Promise<UserPayload>
  /**
   * Clear the client session. `revoke` (default true) also asks the server to
   * revoke the session behind the HttpOnly refresh cookie — only for explicit
   * user logouts. The API layer passes `revoke: false` for sessions the server
   * has already rejected, and never calls this on transient failures.
   */
  logout: (options?: { revoke?: boolean }) => void
}

interface Store extends StoreData, StoreActions {}

export const useUserStore = create<Store>()(
  devtools(
    set => ({
      access_token: INITIAL_USER_PAYLOAD.access_token,
      email: INITIAL_USER_PAYLOAD.email,
      isEmailConfirmed: INITIAL_USER_PAYLOAD.isEmailConfirmed,
      isGuest: INITIAL_USER_PAYLOAD.isGuest,
      plan: INITIAL_USER_PAYLOAD.plan,
      setAccessToken: (access_token): UserPayload => {
        const decoded = decodeJwt<UserPayload>(access_token)
        setSentryUser(decoded)
        set({
          access_token,
          email: decoded.email,
          isEmailConfirmed: decoded.isEmailConfirmed,
          isGuest: decoded.isGuest,
          plan: decoded.plan,
        })

        return decoded
      },
      login: async (access_token, options): Promise<UserPayload> => {
        //if have current session make sure to logout all related data to user
        const userOptions =
          options ??
          (await userControllerGetMyOptions({
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          }))
        useOptionsStore.getState().setData(userOptions)
        activePlayer.stopAll()

        return useUserStore.getState().setAccessToken(access_token)
      },
      logout: ({ revoke = true } = {}) => {
        setSentryUser(null)
        if (revoke) void authControllerLogoutCookie().catch(() => {})
        useOptionsStore.getState().reset()
        activePlayer.stopAll()
        queryClient.removeQueries()
        set({
          access_token: "",
          email: null,
          isEmailConfirmed: false,
          isGuest: false,
          plan: UserPlan.FREE,
        })
      },
    }),
    { name: "UserStore", enabled: process.env.NODE_ENV === "development" }
  )
)

// v1 stored a bearer JWT in localStorage. New sessions use an HttpOnly refresh
// cookie and keep access tokens in memory only. Removing this key is the
// intentional one-time forced logout for legacy sessions.
localStorage.removeItem("user-store")
